import { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { logger } from '@/lib/logger';

/**
 * Lazily loads and renders binary context associated with a question (image/audio).
 * Uses a provided fetcher to obtain base64 content and renders format-specific previews.
 * Only attempts to load binary content for 'audio' and 'image' context types.
 */

interface LazyBinaryContentProps {
  readonly contextId: number;
  readonly mimeType?: string;
  readonly contextTitle?: string;
  readonly contextType?: string;
  readonly mediaUrl?: string | null;
  readonly initialBase64?: string | null;
  readonly fetchBinaryContent: (contextId: number) => Promise<string | null>;
}

export function LazyBinaryContent({
  contextId,
  mimeType,
  contextTitle,
  contextType,
  mediaUrl,
  initialBase64,
  fetchBinaryContent,
}: Readonly<LazyBinaryContentProps>) {
  const { t } = useTranslation();
  const [base64Content, setBase64Content] = useState<string | null>(
    initialBase64 || null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [missingContent, setMissingContent] = useState(false);

  // Only load binary content for audio and image contexts
  const shouldLoadBinary = contextType === "audio" || contextType === "image";
  const hasDirectMedia = Boolean(mediaUrl);

  useEffect(() => {
    // If context type doesn't require binary content, don't load it
    if (!shouldLoadBinary) {
      setLoading(false);
      return;
    }

    // If we already have a media URL or initial base64 payload, no need to fetch
    if (hasDirectMedia || initialBase64) {
      setLoading(false);
      setMissingContent(false);
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadContent = async () => {
      setLoading(true);
      setError(false);
      setMissingContent(false);

      try {
        const content = await fetchBinaryContent(contextId);
        if (mounted) {
          if (content) {
            setBase64Content(content);
          } else {
            // Content is missing - this is expected for some contexts
            // Don't show error, just mark as missing
            setMissingContent(true);
          }
        }
      } catch (err) {
        // API error - only show error if it's not a 404 (missing content)
        logger.error("Error loading binary content:", err);
        if (mounted) {
          setError(true);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadContent();

    return () => {
      mounted = false;
    };
  }, [
    contextId,
    fetchBinaryContent,
    shouldLoadBinary,
    hasDirectMedia,
    initialBase64,
  ]);

  // Don't render anything for text contexts or contexts that don't require binary
  if (!shouldLoadBinary) {
    return null;
  }

  if (hasDirectMedia) {
    if (mimeType?.startsWith("image/")) {
      return (
        <div className="p-3 bg-purple-100 rounded-md border border-purple-300">
          <div className="font-medium text-purple-900 text-sm mb-2">
            {t("admin.answersManager.lazyBinaryContent.imagePreview")}:
          </div>
          <img
            src={mediaUrl || ""}
            alt={
              contextTitle ||
              t("admin.answersManager.lazyBinaryContent.contextPreview")
            }
            className="max-w-full h-auto rounded border"
            style={{ maxHeight: "300px" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      );
    }

    // default to audio player
    return (
      <div className="p-3 bg-purple-100 rounded-md border border-purple-300">
        <div className="font-medium text-purple-900 text-sm mb-2">
          {t("admin.answersManager.lazyBinaryContent.audioPreview")}:
        </div>
        <audio controls className="w-full" src={mediaUrl || undefined}>
          <track kind="captions" />
          {t("admin.answersManager.lazyBinaryContent.browserNotSupported")}
        </audio>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-3 bg-purple-100 rounded-md border border-purple-300">
        <div className="flex items-center gap-2 text-purple-700">
          <Clock className="h-4 w-4 animate-spin" />
          <span className="text-sm">
            {t("admin.answersManager.lazyBinaryContent.loading")}
          </span>
        </div>
      </div>
    );
  }

  // Check for actual errors first (network, server errors, etc.)
  if (error) {
    return (
      <div className="p-3 bg-red-100 rounded-md border border-red-300">
        <div className="text-sm text-red-700">
          {t("admin.answersManager.lazyBinaryContent.loadError")}
        </div>
        <div className="text-xs text-red-600 mt-1">
          {t("admin.answersManager.lazyBinaryContent.loadErrorDescription")}
        </div>
      </div>
    );
  }

  // Missing content is not an error - just show a helpful message
  // This happens when binary content doesn't exist in the database (404 response)
  if (missingContent || !base64Content) {
    return (
      <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
        <div className="text-sm text-amber-700">
          {contextType === "audio"
            ? t("admin.answersManager.lazyBinaryContent.audioNotAvailable")
            : t("admin.answersManager.lazyBinaryContent.imageNotAvailable")}
        </div>
        <div className="text-xs text-amber-600 mt-1">
          {contextType === "audio"
            ? t(
                "admin.answersManager.lazyBinaryContent.audioNotAvailableDescription",
              )
            : t(
                "admin.answersManager.lazyBinaryContent.imageNotAvailableDescription",
              )}
        </div>
      </div>
    );
  }

  if (mimeType?.startsWith("image/")) {
    return (
      <div className="p-3 bg-purple-100 rounded-md border border-purple-300">
        <div className="font-medium text-purple-900 text-sm mb-2">
          {t("admin.answersManager.lazyBinaryContent.imagePreview")}:
        </div>
        <img
          src={`data:${mimeType};base64,${base64Content}`}
          alt={
            contextTitle ||
            t("admin.answersManager.lazyBinaryContent.contextPreview")
          }
          className="max-w-full h-auto rounded border"
          style={{ maxHeight: "300px" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
    );
  }

  if (mimeType?.startsWith("audio/")) {
    return (
      <div className="p-3 bg-purple-100 rounded-md border border-purple-300">
        <div className="font-medium text-purple-900 text-sm mb-2">
          {t("admin.answersManager.lazyBinaryContent.audioPreview")}:
        </div>
        <audio
          controls
          className="w-full"
          src={`data:${mimeType};base64,${base64Content}`}
        >
          <track kind="captions" />
          {t("admin.answersManager.lazyBinaryContent.browserNotSupported")}
        </audio>
      </div>
    );
  }

  return (
    <div className="p-3 bg-purple-100 rounded-md border border-purple-300">
      <div className="text-sm text-purple-700">
        {t("admin.answersManager.lazyBinaryContent.mediaFile", {
          title:
            contextTitle || t("admin.answersManager.lazyBinaryContent.unknown"),
        })}
      </div>
    </div>
  );
}
